import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import Stars from "@/components/Stars";
import { BadgeCheck, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

const URGENCY_STYLES = {
  ASAP: "bg-red-50 text-red-700 border-red-200",
  "Within a Week": "bg-orange-50 text-orange-700 border-orange-200",
  Flexible: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function statusLabel(s) {
  return {
    open: "Open",
    in_progress: "Quote Accepted",
    completed: "Work Completed",
    closed: "Closed",
  }[s] || s;
}

export default function JobDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);

  const load = useCallback(
    () => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false)),
    [id],
  );
  useEffect(() => { load(); }, [load]);

  if (job === null) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;
  if (!job) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Job not found.</div>;

  const isOwner = user && user.id === job.owner_id;
  const canQuote = user && user.role === "tradesperson" && job.status === "open" && !job.quotes.some(q => q.tradesperson_id === user.id);

  const accept = async (quoteId) => {
    try {
      await api.post(`/jobs/${id}/accept-quote/${quoteId}`);
      toast.success("Quote accepted. Job is now in progress.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Card className="border-slate-200">
        <CardContent className="p-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Badge variant="outline" className="border-blue-200 text-blue-900 font-normal">{job.category}</Badge>
            <Badge className={`${URGENCY_STYLES[job.urgency] || "bg-slate-100 text-slate-700"} border font-normal`}>{job.urgency}</Badge>
          </div>
          <h1 className="font-display font-bold text-3xl text-slate-900 mt-3" data-testid="job-title">{job.title}</h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.city}, {job.province}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Posted by {job.owner_name}</span>
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{statusLabel(job.status)}</span>
          </div>

          {job.photos && job.photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {job.photos.map((p) => (
                <img key={p} src={p} alt="" className="aspect-video object-cover rounded-md border border-slate-200" />
              ))}
            </div>
          )}

          <Separator className="my-7" />

          <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>

          {canQuote && (
            <div className="mt-8">
              <QuoteDialog jobId={id} onSubmitted={load} />
            </div>
          )}

          <Separator className="my-7" />

          <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
            Quotes ({job.quotes.length})
          </h2>
          <div className="space-y-3">
            {job.quotes.map((q) => {
              const isAccepted = job.accepted_quote_id === q.id;
              const isRedacted = q.price === null || q.price === undefined;
              return (
                <div
                  key={q.id}
                  data-testid={`quote-${q.id}`}
                  className={`p-5 rounded-lg border ${isAccepted ? "border-emerald-300 bg-emerald-50" : isRedacted ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}
                >
                  {isRedacted ? (
                    <div className="text-sm text-slate-600 italic">
                      Another contractor has submitted a quote (details private).
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{q.tradesperson_name}</span>
                            {q.verified && (<BadgeCheck className="w-4 h-4 text-blue-700" />)}
                          </div>
                          {q.business_name && (<div className="text-sm text-slate-600">{q.business_name}</div>)}
                          <div className="mt-1"><Stars value={q.rating} showValue /> <span className="text-xs text-slate-500 ml-1">({q.review_count})</span></div>
                        </div>
                        <div className="text-right">
                          <div className="font-display font-bold text-xl text-slate-900">${q.price.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">~{q.estimated_days} day{q.estimated_days === 1 ? "" : "s"}</div>
                        </div>
                      </div>
                      <p className="text-slate-700 text-sm mt-3">{q.message}</p>
                      {isOwner && job.status === "open" && (
                        <Button
                          data-testid={`accept-quote-${q.id}`}
                          size="sm"
                          onClick={() => accept(q.id)}
                          className="mt-3 bg-blue-900 hover:bg-blue-800 text-white"
                        >
                          Accept Quote
                        </Button>
                      )}
                      {isAccepted && (
                        <Badge className="mt-3 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Accepted</Badge>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {job.quotes.length === 0 && job.quote_count > 0 && (
              <div className="text-slate-500 text-sm italic" data-testid="quote-count-only">
                {job.quote_count} quote{job.quote_count === 1 ? "" : "s"} submitted — details are private between the homeowner and each contractor.
              </div>
            )}
            {job.quotes.length === 0 && !job.quote_count && (<div className="text-slate-500 text-sm">No quotes yet.</div>)}
          </div>

          {isOwner && job.status === "in_progress" && (
            <div className="mt-8 flex flex-wrap gap-3">
              <CompleteJobDialog jobId={id} onDone={load} />
              <CloseJobDialog jobId={id} onDone={load} />
            </div>
          )}
          {isOwner && job.status === "open" && (
            <div className="mt-8">
              <CloseJobDialog jobId={id} onDone={load} label="Close this job" reasonPrompt="Why are you closing this job?" />
            </div>
          )}

          {job.review && (
            <div className="mt-8 p-5 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Homeowner Review</div>
              <Stars value={job.review.rating} showValue />
              <p className="text-slate-700 mt-2">{job.review.comment}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QuoteDialog({ jobId, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [msg, setMsg] = useState("");
  const [days, setDays] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/jobs/${jobId}/quotes`, {
        price: parseFloat(price),
        message: msg,
        estimated_days: parseInt(days, 10),
      });
      toast.success("Quote sent");
      setOpen(false);
      setPrice(""); setMsg(""); setDays("");
      onSubmitted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="send-quote-btn" className="bg-orange-600 hover:bg-orange-700 text-white">Send Quote</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Send a Quote</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Price estimate (CAD)</Label>
            <Input data-testid="quote-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Message to homeowner</Label>
            <Textarea data-testid="quote-message" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Estimated days to complete</Label>
            <Input data-testid="quote-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="quote-submit" onClick={submit} disabled={loading || !price || !msg || !days} className="bg-orange-600 hover:bg-orange-700 text-white">
            {loading ? "Sending…" : "Send Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteJobDialog({ jobId, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const submit = async () => {
    try {
      await api.post(`/jobs/${jobId}/complete`, { status: "completed", rating, comment });
      toast.success("Job marked complete. Thanks for your review!");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="complete-job-btn" className="bg-blue-900 hover:bg-blue-800 text-white">Mark Work Completed</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Leave a review</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Rating</Label>
            <div className="flex items-center gap-1 mt-2">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`rating-${n}`}
                  onClick={() => setRating(n)}
                  className={`p-1 ${n <= rating ? "text-orange-500" : "text-slate-300"}`}
                >★</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Your review</Label>
            <Textarea data-testid="review-comment" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="review-submit" onClick={submit} disabled={!comment} className="bg-orange-600 hover:bg-orange-700 text-white">Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseJobDialog({ jobId, onDone, label = "Close job (no work completed)", reasonPrompt = "Why are you closing this job?" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const submit = async () => {
    try {
      await api.post(`/jobs/${jobId}/complete`, { status: "closed", closed_reason: reason });
      toast.success("Job closed.");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="close-job-btn" variant="outline" className="border-slate-300">
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Close this job</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 -mt-2">
          Closing a job without &quot;Work Completed&quot; means no review is left. Use this if you decided not to hire, went with someone off-platform, or the project was cancelled.
        </p>
        <div>
          <Label>{reasonPrompt} <span className="text-xs text-slate-500 font-normal">(optional)</span></Label>
          <Textarea data-testid="close-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" />
        </div>
        <DialogFooter>
          <Button data-testid="close-job-submit" onClick={submit} variant="destructive">Confirm close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
